import React, { useEffect, useState, useRef } from 'react';
import { alertsAPI, statsAPI, sitesAPI } from '../services/api';
import { useTheme, T } from '../context/ThemeContext';
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Drives the "cool" camera behavior Eran asked for: when a brand-new alert lands,
// fly in tight on that specific site first; then, if other alerts are still active
// too, pull back out a couple seconds later so all of them are visible together.
// After that the operator has the map to themselves until the next new alert.
function MapController({ alerts, sites }) {
  const map = useMap();
  const prevIdsRef = useRef(new Set());
  const didInitialFit = useRef(false);

  const siteCoords = (siteId) => {
    const s = sites.find(s => s.id === siteId);
    if (!s || !s.lat || !s.lng) return null;
    return [parseFloat(s.lat), parseFloat(s.lng)];
  };

  useEffect(() => {
    const currentIds = new Set(alerts.map(a => a.id));
    const isFirstRun = !didInitialFit.current;
    const newOnes = alerts.filter(a => !prevIdsRef.current.has(a.id));
    prevIdsRef.current = currentIds;

    const activePoints = alerts.map(a => siteCoords(a.site_id)).filter(Boolean);

    if (isFirstRun) {
      didInitialFit.current = true;
      // page just loaded - frame whatever's already active, no need for the zoom-in dance
      if (activePoints.length > 1) map.fitBounds(activePoints, { padding: [50,50], maxZoom: 16 });
      else if (activePoints.length === 1) map.setView(activePoints[0], 16);
      return;
    }

    if (newOnes.length === 0) return;
    const newest = newOnes[newOnes.length - 1];
    const coords = siteCoords(newest.site_id);
    if (!coords) return;

    map.flyTo(coords, 17, { duration: 1.2 });

    // If this new alert's button sits on a floor plan, the parent shows a small
    // floating window over the map ~2.4s later (see floorplanOverlayId below) -
    // pull the camera back out to the general site overview at the same moment,
    // per Eran, so the small window floats over the whole map rather than a
    // single tight-zoomed site.
    const hasFloorplan = newest.floorplan_id && (newest.site_floorplans||[]).length;
    const t = setTimeout(() => {
      if (hasFloorplan) {
        const allPoints = sites.filter(s=>s.lat&&s.lng).map(s=>[parseFloat(s.lat),parseFloat(s.lng)]);
        if (allPoints.length > 1) map.flyToBounds(allPoints, { padding: [60,60], maxZoom: 14, duration: 1.2 });
        else if (allPoints.length === 1) map.flyTo(allPoints[0], 13, { duration: 1.2 });
      } else if (activePoints.length > 1) {
        map.flyToBounds(activePoints, { padding: [60,60], maxZoom: 15, duration: 1.2 });
      }
    }, 2400);
    return () => clearTimeout(t);
  }, [alerts.map(a => a.id).join(','), sites.length]);

  return null;
}

export default function MokedPage({ user }) {
  const { dark, setDark, lang, setLang } = useTheme();
  const t = T[lang];
  const rtl = lang === 'he';

  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({});
  const [flash, setFlash] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [mapSites, setMapSites] = useState([]);
  const [mutedIds, setMutedIds] = useState(() => new Set());
  const [quickAlert, setQuickAlert] = useState(null);
  const [floorplanOverlayId, setFloorplanOverlayId] = useState(null);
  const floorplanShownIdsRef = useRef(new Set());
  const floorplanFirstRunRef = useRef(true);

  useEffect(() => {
    const loadSites = () => sitesAPI.getAll().then(r => setMapSites(r.data)).catch(() => {});
    loadSites();
    // sites (new ones especially) can be created by an admin at any time while this
    // screen is open - refetch periodically so a newly-created site's map location
    // and any alert on it actually show up without needing a full page reload.
    const iv = setInterval(loadSites, 15000);
    return () => clearInterval(iv);
  }, []);

  const geoSites = mapSites.filter(s => s.lat && s.lng);
  const mapCenter = geoSites.length
    ? [geoSites.reduce((s,x)=>s+parseFloat(x.lat),0)/geoSites.length, geoSites.reduce((s,x)=>s+parseFloat(x.lng),0)/geoSites.length]
    : [31.7683, 35.2137]; // Israel center fallback
  const mapZoom = geoSites.length > 1 ? 12 : geoSites.length === 1 ? 15 : 8;

  const c = dark ? {
    bg:'#060d1a', topbar:'#0a1628', card:'#0a1628', border:'#1e3a5f',
    text:'#fff', sub:'#94a3b8', stat:'#0a1628', map:'#060d1a',
    modal:'#0f1f38', notes:'#060d1a', closeBg:'#1a2e4a', closeTxt:'#cbd5e1',
  } : {
    bg:'#f0f4f8', topbar:'#1e40af', card:'#ffffff', border:'#bfdbfe',
    text:'#1e293b', sub:'#475569', stat:'#ffffff', map:'#e2e8f0',
    modal:'#ffffff', notes:'#f8fafc', closeBg:'#e2e8f0', closeTxt:'#475569',
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [aRes, sRes] = await Promise.all([alertsAPI.getActive(), statsAPI.get()]);
        const a = aRes.data, s = sRes.data;
        setAlerts(prev => { if (prev.length > 0 && a.length > prev.length) setFlash(true); return a; });
        setStats(s);
      } catch {}
    };
    load();
    const iv = setInterval(load, 3000);
    return () => clearInterval(iv);
  }, []);

  // One persistent AudioContext, created lazily on first use and reused for every
  // beep - creating+closing a fresh context on every single beep (the original
  // approach) added a noticeable startup lag before sound actually played, since
  // spinning up a browser audio graph from scratch isn't instant.
  const audioCtxRef = useRef(null);
  const getAudioCtx = () => {
    if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();
    return audioCtxRef.current;
  };
  const playAlarm = () => {
    try {
      const ctx = getAudioCtx();
      const dur = 1.2;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sawtooth';
      gain.gain.value = 0.28;
      osc.connect(gain);
      gain.connect(ctx.destination);
      const now = ctx.currentTime;
      for (let t = 0; t < dur; t += 0.15) {
        osc.frequency.setValueAtTime((t / 0.15) % 2 < 1 ? 1250 : 850, now + t);
      }
      osc.start(now);
      osc.stop(now + dur);
    } catch (e) {}
  };

  useEffect(() => {
    if (flash) { const t = setTimeout(() => setFlash(false), 3000); return () => clearTimeout(t); }
  }, [flash]);

  // Alarm keeps repeating as long as there's at least one unmuted 'new' alert -
  // it must NOT stop on its own just because a few seconds passed (per Eran: need
  // certainty someone actually noticed it before it goes quiet).
  useEffect(() => {
    const hasUnmutedNew = alerts.some(a => a.status === 'new' && !mutedIds.has(a.id));
    if (!hasUnmutedNew) return;
    playAlarm();
    const iv = setInterval(playAlarm, 1500);
    return () => clearInterval(iv);
  }, [alerts, mutedIds]);

  const reload = async () => {
    const [aRes, sRes] = await Promise.all([alertsAPI.getActive(), statsAPI.get()]);
    setAlerts(aRes.data); setStats(sRes.data); setSelected(null); setNotes(''); setShowNotes(false);
  };

  // Site map already flies/zooms in on a brand-new alert (MapController above) - if that
  // alert's button has a floor-plan pin, cover the map with the floor plan ~2.4s later
  // (same delay the camera uses before pulling back out) so the two read as one sequence:
  // zoom in on the building, then reveal exactly where inside it. No click needed, and it
  // auto-hides itself once the alert is no longer active (resolved / no longer in `alerts`).
  useEffect(() => {
    if (floorplanFirstRunRef.current) {
      floorplanFirstRunRef.current = false;
      alerts.forEach(a => floorplanShownIdsRef.current.add(a.id));
      return;
    }
    const newOnes = alerts.filter(a => a.status === 'new' && a.floorplan_id && (a.site_floorplans||[]).length && !floorplanShownIdsRef.current.has(a.id));
    if (newOnes.length === 0) return;
    const targetId = newOnes[newOnes.length - 1].id;
    const timer = setTimeout(() => {
      floorplanShownIdsRef.current.add(targetId);
      setFloorplanOverlayId(targetId);
    }, 2400);
    return () => clearTimeout(timer);
  }, [alerts]);
  const floorplanOverlayAlert = alerts.find(a => a.id === floorplanOverlayId) || null;

  const ack = async () => { setLoading(true); try { await alertsAPI.acknowledge(selected.id); await reload(); } finally { setLoading(false); } };

  // Opens the manage-event modal. If the alert is still 'new', acknowledging it first
  // (color changes red->orange immediately) and showing the notes box right away -
  // per Eran, the separate "in-treatment" middle step just adds friction, not value.
  const openManageEvent = async (alert) => {
    setQuickAlert(null);
    setNotes('');
    let current = alert;
    if (alert.status === 'new') {
      setLoading(true);
      try {
        await alertsAPI.acknowledge(alert.id);
        const fresh = await alertsAPI.getActive();
        setAlerts(fresh.data);
        current = fresh.data.find(a => a.id === alert.id) || alert;
      } catch (e) {}
      setLoading(false);
    }
    setSelected(current);
    setShowNotes(true);
  };

  const resolve = async () => {
    if (!notes.trim()) { alert(rtl ? 'יש להזין תיאור דרכי הפעולה שבוצעו לפני סגירת האירוע' : 'Please describe the actions taken before closing the event'); return; }
    setLoading(true);
    try {
      const closedId = selected.id;
      await alertsAPI.resolve(closedId, notes);
      // verify the close actually took effect server-side, not just trust a 200 response
      const check = await alertsAPI.getActive();
      const stillActive = check.data.some(a => a.id === closedId);
      if (stillActive) { alert(rtl ? 'שגיאה: האירוע לא נסגר בפועל, נסה שוב' : 'Error: the event did not actually close, please try again'); }
      else { alert(rtl ? '✓ האירוע נסגר בהצלחה' : '✓ Event closed successfully'); }
      await reload();
    } catch (e) {
      alert(rtl ? 'שגיאה בסגירת האירוע: ' + e.message : 'Error closing event: ' + e.message);
    } finally { setLoading(false); }
  };
  const onClose = () => { setSelected(null); setNotes(''); setShowNotes(false); };

  // Clicking a site marker that has no active alert should still show everything
  // known about that site (address, contacts, entry code, notes...) — just in
  // read-only mode, no ack/resolve/notes controls since there's nothing to manage.
  const openSiteDetails = (site) => {
    setQuickAlert(null);
    setSelected({
      site_name: site.name,
      site_address: site.address,
      site_city: site.city,
      entry_code: site.entry_code,
      contacts: site.contacts || [],
      site_notes: site.site_notes,
      site_floorplans: site.floorplans || [],
      status: null,
    });
    setShowNotes(false);
  };

  const ST = {
    new:          { label: rtl ? 'חדש'          : 'New',         color: '#ef4444' },
    acknowledged: { label: rtl ? 'בטיפול'       : 'Acknowledged', color: '#eab308' },
    in_treatment: { label: rtl ? 'בטיפול מתקדם' : 'In Treatment', color: '#3b82f6' },
  };
  const statusMeta = (status) => ST[status] || { label: rtl ? 'אין אירוע פעיל' : 'No active incident', color: '#3b82f6' };

  const btn = (onClick, bg, color, border, text, disabled) => (
    <button onClick={onClick} disabled={disabled}
      style={{background:bg, color, border:`2px solid ${border}`,
        borderRadius:7, padding:'9px 16px', fontSize:13, fontWeight:700, cursor:'pointer'}}>
      {text}
    </button>
  );

  return (
    <div style={{display:'flex', flexDirection:'column', height:'100vh',
      fontFamily:'system-ui,Arial,sans-serif', background:c.bg,
      color:c.text, direction: rtl ? 'rtl' : 'ltr'}}>
      <style>{`*{box-sizing:border-box} @keyframes blink{0%,100%{opacity:1}50%{opacity:0.3}}`}</style>

      {/* ── Topbar ── */}
      <div style={{height:54, background:c.topbar, borderBottom:`2px solid ${c.border}`,
        display:'flex', alignItems:'center', justifyContent:'space-between',
        padding:'0 20px', flexShrink:0, gap:10}}>

        {/* לוגו — תמיד בצד החיצוני הנכון */}
        <div style={{display:'flex', alignItems:'center', gap:10, flexShrink:0}}>
          <div style={{width:36, height:36, background:'#f97316', borderRadius:8,
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:13, fontWeight:800, color:'#fff'}}>SS</div>
          <div>
            <div style={{fontSize:15, fontWeight:800, color:'#fff', letterSpacing:1}}>SEM SIM</div>
            <div style={{fontSize:9, color:'#f97316', letterSpacing:1.5, fontWeight:700}}>CUTTING-EDGE WIRELESS</div>
          </div>
        </div>

        {/* LIVE badge — מרכז */}
        <div style={{display:'flex', alignItems:'center', gap:7, background:c.stat,
          border:`1px solid ${c.border}`, borderRadius:50, padding:'5px 16px', flexShrink:0}}>
          <div style={{width:8, height:8, borderRadius:'50%', background:'#22c55e', animation:'blink 1.5s infinite'}}/>
          <span style={{fontSize:11, color:'#22c55e', fontWeight:700, letterSpacing:1}}>LIVE</span>
          {user?.municipality && <>
            <span style={{color:c.border}}>|</span>
            <span style={{fontSize:12, color:c.closeTxt, fontWeight:500}}>{user.municipality}</span>
          </>}
        </div>

        {/* כפתורים ימין */}
        <div style={{display:'flex', alignItems:'center', gap:8, flexShrink:0}}>
          <span style={{fontSize:13, color:c.text, fontWeight:600, background:c.stat,
            border:`1px solid ${c.border}`, borderRadius:6, padding:'4px 10px',
            fontVariantNumeric:'tabular-nums', minWidth:72, textAlign:'center'}}>
            {new Date().toLocaleTimeString(rtl ? 'he-IL' : 'en-US')}
          </span>

          {alerts.length > 0 &&
            <span style={{background:'#ef4444', color:'#fff', borderRadius:6,
              padding:'4px 10px', fontSize:12, fontWeight:700}}>
              🚨 {alerts.length}
            </span>}

          <button onClick={() => setLang(rtl ? 'en' : 'he')}
            style={{background:c.stat, border:`1px solid ${c.border}`, color:c.text,
              borderRadius:6, padding:'5px 11px', fontSize:12, cursor:'pointer', fontWeight:700}}>
            {rtl ? 'EN' : 'עב'}
          </button>

          <button onClick={() => setDark(!dark)}
            style={{background:c.stat, border:`1px solid ${c.border}`, color:c.text,
              borderRadius:6, padding:'5px 11px', fontSize:14, cursor:'pointer'}}>
            {dark ? '☀️' : '🌙'}
          </button>

          <button onClick={() => window.location.href = '/reports'}
            style={{background:c.stat, border:`1px solid ${c.border}`, color:'#a78bfa',
              borderRadius:6, padding:'5px 11px', fontSize:11, cursor:'pointer', fontWeight:700}}>
            {lang === 'he' ? 'דוחות' : 'Reports'}
          </button>
          <button onClick={() => window.location.href = '/admin'}
            style={{background:c.stat, border:`1px solid ${c.border}`, color:'#94a3b8',
              borderRadius:6, padding:'5px 11px', fontSize:11, cursor:'pointer', fontWeight:600}}>
            {t.adminPanel}
          </button>

          <button onClick={()=>navigate('/profile')} style={{background:'transparent',border:`1px solid ${c.border}`,color:'#64748b',borderRadius:6,padding:'5px 10px',fontSize:11,cursor:'pointer',marginRight:4}}>{lang==='he'?'פרופיל':'Profile'}</button><button onClick={() => { localStorage.removeItem('semsim_token'); window.location.href = '/'; }}
            style={{background:'transparent', border:`1px solid ${c.border}`, color:'#64748b',
              borderRadius:6, padding:'5px 10px', fontSize:11, cursor:'pointer'}}>
            {t.logout}
          </button>
        </div>
      </div>

      {/* ── Stats bar ── */}
      <div style={{background:c.card, borderBottom:`2px solid ${c.border}`, display:'flex', flexShrink:0}}>
        {[
          ['#f87171', stats.active_new||0, rtl ? 'חדשות'     : 'New'],
          ['#fdba74', stats.resolved_today||0, rtl ? 'טופלו'     : 'Treated'],
          ['#4ade80', stats.total_sites||0, rtl ? 'סה״כ אתרים': 'Total Sites'],
          ['#60a5fa', stats.alerts_today||0,rtl ? 'היום'      : 'Today'],
        ].map(([color, count, label]) => (
          <div key={label} style={{flex:1, display:'flex', flexDirection:'column',
            alignItems:'center', padding:'8px 0', borderInlineEnd:`1px solid ${c.border}`}}>
            <span style={{fontSize:22, fontWeight:800, color}}>{count ?? 0}</span>
            <span style={{fontSize:11, fontWeight:700, color, marginTop:1, opacity:0.8}}>{label}</span>
          </div>
        ))}
        <div style={{display:'flex', alignItems:'center', padding:'0 16px',
          borderInlineEnd:`1px solid ${c.border}`}}>
          <span style={{background:c.stat, border:'1px solid #f97316',
            color:'#fb923c', borderRadius:6, padding:'4px 12px', fontSize:11, fontWeight:600}}>
            {t.mokedTitle}
          </span>
        </div>
      </div>

      {/* ── Flash ── */}
      {flash && (
        <div style={{background:'#ef4444', color:'#fff', textAlign:'center', padding:'8px',
          fontSize:13, fontWeight:700, borderBottom:'2px solid #fca5a5', flexShrink:0}}>
          🚨 {t.liveAlerts}!
        </div>
      )}

      {/* ── Main ── */}
      <div style={{display:'flex', flex:1, overflow:'hidden', flexDirection: rtl ? 'row' : 'row-reverse'}}>

        {/* Map */}
        <div style={{flex:1, position:'relative', background:c.map, overflow:'hidden'}}>
          {floorplanOverlayAlert && (
            <div style={{position:'absolute', top:14, insetInlineEnd:14, zIndex:950, width:330, maxWidth:'85%',
              maxHeight:'82%', overflowY:'auto', background:c.modal,
              border:`2px solid ${(ST[floorplanOverlayAlert.status]||ST.new).color}`,
              borderRadius:12, boxShadow:'0 10px 34px rgba(0,0,0,0.5)'}}>
              <div style={{padding:'9px 12px', background:c.topbar, display:'flex', justifyContent:'space-between',
                alignItems:'center', borderBottom:`2px solid ${c.border}`, position:'sticky', top:0, zIndex:1}}>
                <div style={{color:'#fff', fontWeight:800, fontSize:12}}>🖼 {floorplanOverlayAlert.site_name}</div>
                <button onClick={() => setFloorplanOverlayId(null)}
                  style={{background:'transparent', border:'none', color:'#fff', fontSize:16,
                    cursor:'pointer', fontWeight:700, lineHeight:1}}>×</button>
              </div>
              <div style={{padding:12, display:'flex', flexDirection:'column', gap:10}}>
                {(floorplanOverlayAlert.site_floorplans||[]).map(fp => (
                  <div key={fp.id}>
                    <div style={{fontSize:11, color:c.sub, fontWeight:700, marginBottom:5}}>{fp.label}</div>
                    <div style={{position:'relative', display:'block', border:`1px solid ${c.border}`, borderRadius:8, overflow:'hidden'}}>
                      <img src={fp.image_path} alt={fp.label} style={{display:'block', width:'100%'}}/>
                      {floorplanOverlayAlert.floorplan_id === fp.id && floorplanOverlayAlert.pin_x != null && floorplanOverlayAlert.pin_y != null && (
                        <div style={{position:'absolute', left:`${parseFloat(floorplanOverlayAlert.pin_x)*100}%`,
                          top:`${parseFloat(floorplanOverlayAlert.pin_y)*100}%`, transform:'translate(-50%,-50%)',
                          width:28, height:28, borderRadius:'50%', background:'#ef4444', border:'3px solid #fff',
                          boxShadow:'0 0 14px #ef4444', animation:'blink 1s infinite',
                          display:'flex', alignItems:'center', justifyContent:'center', fontSize:13}}>🚨</div>
                      )}
                    </div>
                  </div>
                ))}
                <button onClick={() => setMutedIds(prev => {
                    const next = new Set(prev);
                    if (next.has(floorplanOverlayAlert.id)) next.delete(floorplanOverlayAlert.id); else next.add(floorplanOverlayAlert.id);
                    return next;
                  })}
                  style={{background:mutedIds.has(floorplanOverlayAlert.id)?'#374151':'#7c2d12', color:'#fed7aa',
                    border:'2px solid #fb923c', borderRadius:7, padding:'8px 12px', fontSize:12,
                    fontWeight:700, cursor:'pointer'}}>
                  {mutedIds.has(floorplanOverlayAlert.id) ? (rtl?'🔊 בטל השתקה':'🔊 Unmute Sound') : (rtl?'🔇 השתק סאונד':'🔇 Mute Sound')}
                </button>
                <button onClick={() => { setFloorplanOverlayId(null); openManageEvent(floorplanOverlayAlert); }}
                  style={{background:'#1d4ed8', color:'#fff', border:'2px solid #3b82f6', borderRadius:7,
                    padding:'8px 12px', fontSize:12, fontWeight:700, cursor:'pointer'}}>
                  📋 {rtl?'נהל אירוע':'Manage Event'}
                </button>
              </div>
            </div>
          )}
          {mapSites.filter(s=>s.lat&&s.lng).length === 0 ? (
            <div style={{position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center'}}>
              <div style={{textAlign:'center', color:c.sub}}>
                <div style={{fontSize:48, marginBottom:8}}>🗺️</div>
                <div style={{fontSize:14, fontWeight:600}}>{rtl?'אין עדיין אתרים עם מיקום על המפה':'No sites with a map location yet'}</div>
                <div style={{fontSize:12, marginTop:4}}>{rtl?'הכתובת של אתר ממוקמת אוטומטית כשיוצרים/עורכים אתר':'A site\'s location is set automatically when created/edited'}</div>
              </div>
            </div>
          ) : (
            <MapContainer center={mapCenter} zoom={mapZoom} style={{height:'100%', width:'100%'}} key={mapSites.length}>
              <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <MapController alerts={alerts} sites={mapSites} />
              {mapSites.filter(s=>s.lat&&s.lng).map(site => {
                // Every site keeps the same permanent alarm-style icon at all times -
                // per Eran, no separate "small dot vs big emoji" distinction; only the
                // color (and pulse, for a brand-new unhandled alert) should change:
                // blue = no incident, yellow = being handled (acknowledged), red = new.
                const siteAlertsHere = alerts.filter(a => a.site_id === site.id);
                const siteAlert = siteAlertsHere[0];
                const color = siteAlert ? (ST[siteAlert.status]||ST.new).color : '#3b82f6';
                const isNew = siteAlert && siteAlert.status === 'new';
                const badge = siteAlertsHere.length > 1 ? siteAlertsHere.length : '🚨';
                const icon = L.divIcon({
                  className: '',
                  html: `<div style="width:34px;height:34px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 0 10px ${color};display:flex;align-items:center;justify-content:center;font-size:15px;${isNew?'animation:blink 1s infinite;':''}">${badge}</div>`,
                  iconSize: [34, 34],
                  iconAnchor: [17, 17],
                });
                const siteFloorplans = site.floorplans || [];
                return (
                  <Marker key={site.id} position={[parseFloat(site.lat), parseFloat(site.lng)]} icon={icon}
                    eventHandlers={{ click: () => siteAlert ? setQuickAlert(siteAlert) : openSiteDetails(site) }}>
                    {/* Hover preview is for the resting/no-incident state only - once there's an
                        active alert, the automatic floorplanOverlay card already shows the floor
                        plan on its own, so also popping up this tooltip on hover just doubled up
                        as two near-identical windows on screen (caught live by Eran 2026-07-23). */}
                    {!siteAlert && siteFloorplans.length > 0 && (
                      <Tooltip direction="top" offset={[0,-18]} opacity={1}>
                        <div style={{width:200}}>
                          <div style={{fontWeight:700, fontSize:11, marginBottom:4}}>{site.name}</div>
                          <img src={siteFloorplans[0].image_path} alt={siteFloorplans[0].label}
                            style={{display:'block', width:'100%', borderRadius:4}}/>
                          <div style={{fontSize:10, marginTop:3, opacity:0.75}}>
                            {siteFloorplans[0].label}{siteFloorplans.length > 1 ? ` +${siteFloorplans.length-1} ${rtl?'נוספות':'more'}` : ''}
                          </div>
                        </div>
                      </Tooltip>
                    )}
                  </Marker>
                );
              })}
            </MapContainer>
          )}
        </div>

        {/* רשימת התראות — ימין בעברית, שמאל באנגלית */}
        <div style={{width:300, background:c.card, borderInlineEnd:`2px solid ${c.border}`,
          overflowY:'auto', flexShrink:0}}>
          <div style={{padding:'10px 14px', borderBottom:`1px solid ${c.border}`,
            fontSize:13, fontWeight:700, color:c.sub, letterSpacing:0.5}}>
            📋 {t.liveAlerts} ({alerts.length})
          </div>
          {alerts.length === 0 && (
            <div style={{padding:24, textAlign:'center', color:c.sub, fontSize:13}}>
              {t.noAlerts}
            </div>
          )}
          {alerts.map(a => {
            const s = ST[a.status] || ST.new;
            const isSel = selected?.id === a.id || quickAlert?.id === a.id;
            return (
              <div key={a.id} onClick={() => setQuickAlert(a)}
                style={{padding:'10px 14px', borderBottom:`1px solid ${c.border}`,
                  cursor:'pointer', transition:'background 0.15s',
                  background: isSel ? c.stat : 'transparent',
                  borderInlineStart: isSel ? `3px solid ${s.color}` : '3px solid transparent'}}>
                <div style={{display:'flex', justifyContent:'space-between', marginBottom:3}}>
                  <span style={{fontSize:13, fontWeight:700, color:c.text}}>{a.site_name}</span>
                  <span style={{fontSize:10, background:s.color, color:'#fff',
                    borderRadius:4, padding:'1px 6px', fontWeight:700}}>{s.label}</span>
                </div>
                <div style={{fontSize:11, color:c.sub}}>{a.site_address}</div>
                <div style={{fontSize:13, color:s.color, marginTop:3, fontWeight:800, fontVariantNumeric:'tabular-nums'}}>
                  🕐 {new Date(a.triggered_at).toLocaleTimeString(rtl ? 'he-IL' : 'en-US', {hour:'2-digit',minute:'2-digit',second:'2-digit'})}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Quick action card (map pin / list click) ── */}
      {quickAlert && (
        <div style={{position:'fixed', bottom:24, insetInlineEnd:24, zIndex:1000,
          background:c.modal, border:`2px solid ${(ST[quickAlert.status]||ST.new).color}`,
          borderRadius:12, padding:18, width:290, boxShadow:'0 10px 34px rgba(0,0,0,0.45)'}}>
          <button onClick={()=>setQuickAlert(null)}
            style={{position:'absolute', top:8, insetInlineEnd:8, background:'transparent',
              border:'none', color:c.sub, fontSize:16, cursor:'pointer', fontWeight:700, lineHeight:1}}>×</button>
          <div style={{fontWeight:800, fontSize:14, color:c.text, marginBottom:6, paddingInlineEnd:16}}>{quickAlert.site_name}</div>
          <div style={{fontSize:12, color:c.sub, marginBottom:14}}>
            📍 {quickAlert.site_address}{quickAlert.site_city ? ', ' + quickAlert.site_city : ''}
          </div>
          <div style={{display:'flex', flexDirection:'column', gap:8}}>
            <button onClick={() => setMutedIds(prev => {
                const next = new Set(prev);
                if (next.has(quickAlert.id)) next.delete(quickAlert.id); else next.add(quickAlert.id);
                return next;
              })}
              style={{background:mutedIds.has(quickAlert.id)?'#374151':'#7c2d12', color:'#fed7aa',
                border:'2px solid #fb923c', borderRadius:7, padding:'9px 14px', fontSize:13,
                fontWeight:700, cursor:'pointer'}}>
              {mutedIds.has(quickAlert.id) ? (rtl?'🔊 בטל השתקה':'🔊 Unmute Sound') : (rtl?'🔇 השתק סאונד':'🔇 Mute Sound')}
            </button>
            <button onClick={() => openManageEvent(quickAlert)}
              style={{background:'#1d4ed8', color:'#fff', border:'2px solid #3b82f6',
                borderRadius:7, padding:'9px 14px', fontSize:13, fontWeight:700, cursor:'pointer'}}>
              📋 {rtl?'נהל אירוע':'Manage Event'}
            </button>
          </div>
        </div>
      )}

      {/* ── Modal ── */}
      {selected && (
        <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.75)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:1100}}
          onClick={e => e.target === e.currentTarget && onClose()}>
          <div style={{background:c.modal, border:`2px solid ${c.border}`,
            borderRadius:12, width:390, maxWidth:'95vw', maxHeight:'88vh', overflowY:'auto',
            direction: rtl ? 'rtl' : 'ltr'}}>

            {/* Modal header */}
            <div style={{padding:'13px 18px', background:c.topbar,
              display:'flex', alignItems:'center', justifyContent:'space-between'}}>
              <span style={{fontSize:16, fontWeight:700, color:'#fff'}}>{selected.site_name}</span>
              <div style={{display:'flex', alignItems:'center', gap:8}}>
                <span style={{background:statusMeta(selected.status).color, color:'#fff',
                  fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:6}}>
                  {statusMeta(selected.status).label}
                </span>
                <button onClick={onClose}
                  style={{background:c.closeBg, border:`1px solid ${c.border}`,
                    color:'#94a3b8', width:28, height:28, borderRadius:6,
                    cursor:'pointer', fontSize:15, fontWeight:700, lineHeight:1}}>×</button>
              </div>
            </div>

            {/* Modal body */}
            <div style={{padding:'10px 18px'}}>
              {[
                ['📍', `${selected.site_address}${selected.site_city ? ', '+selected.site_city : ''}`],
                ['🏢', [selected.floor && `${rtl?'קומה':'Floor'} ${selected.floor}`, selected.room && `${rtl?'חדר':'Room'} ${selected.room}`].filter(Boolean).join(' | ')],
                ['🍄', selected.button_type],
                ['🔑', selected.entry_code && `${rtl?'קוד כניסה':'Entry code'}: ${selected.entry_code}`],
                ['📝', selected.site_notes],
                ['🕐', selected.triggered_at && new Date(selected.triggered_at).toLocaleTimeString(rtl?'he-IL':'en-US')],
              ].filter(([,v]) => v).map(([icon, val], i) => (
                <div key={i} style={{display:'flex', alignItems:'center', gap:10,
                  padding:'7px 0', borderBottom:`1px solid ${c.border}`}}>
                  <span style={{fontSize:15, width:22, textAlign:'center', flexShrink:0}}>{icon}</span>
                  <span style={{fontSize:13, color:c.sub}}>{val}</span>
                </div>
              ))}
              {/* Every contact person for this site - if the first one doesn't answer,
                  the operator has the rest right here to try next. */}
              {(selected.contacts||[]).map((ct,i) => (
                <div key={ct.id||i} style={{display:'flex', alignItems:'center', gap:10,
                  padding:'7px 0', borderBottom:`1px solid ${c.border}`}}>
                  <span style={{fontSize:15, width:22, textAlign:'center', flexShrink:0}}>👤</span>
                  <span style={{fontSize:13, color:c.sub, flex:1}}>
                    {ct.full_name}{ct.role_label ? ` (${ct.role_label})` : ''}
                  </span>
                  <a href={`tel:${ct.phone}`} style={{fontSize:13, color:'#3b82f6', fontWeight:700, textDecoration:'none'}}>📞 {ct.phone}</a>
                </div>
              ))}
            </div>

            {/* Floor-plan sketches - all floors shown together on this same screen (no
                per-floor tabs/navigation, per Eran's explicit ask), with the exact pin
                for the button that fired blinking on whichever floor it's on. Sites with
                no floor plans configured just don't render this block at all. */}
            {(selected.site_floorplans||[]).length > 0 && (
              <div style={{padding:'10px 18px', borderTop:`1px solid ${c.border}`}}>
                <div style={{fontSize:12, fontWeight:700, color:c.sub, marginBottom:8}}>
                  🖼 {rtl?'תרשים המבנה':'Building floor plan'}
                </div>
                <div style={{display:'flex', flexDirection:'column', gap:10}}>
                  {selected.site_floorplans.map(fp => (
                    <div key={fp.id}>
                      <div style={{fontSize:11, color:c.sub, marginBottom:4, fontWeight:600}}>{fp.label}</div>
                      <div style={{position:'relative', display:'inline-block', width:'100%',
                        border:`1px solid ${c.border}`, borderRadius:8, overflow:'hidden'}}>
                        <img src={fp.image_path} alt={fp.label} style={{display:'block', width:'100%', maxHeight:'32vh', objectFit:'contain'}}/>
                        {selected.floorplan_id === fp.id && selected.pin_x != null && selected.pin_y != null && (
                          <div style={{position:'absolute', left:`${parseFloat(selected.pin_x)*100}%`,
                            top:`${parseFloat(selected.pin_y)*100}%`, transform:'translate(-50%,-50%)',
                            width:26, height:26, borderRadius:'50%', background:'#ef4444',
                            border:'3px solid #fff', boxShadow:'0 0 10px #ef4444',
                            animation: selected.status==='new' ? 'blink 1s infinite' : 'none'}}/>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {showNotes && (
              <div style={{padding:'0 18px 10px'}}>
                <textarea placeholder={t.notesPlaceholder} value={notes}
                  onChange={e => setNotes(e.target.value)}
                  style={{width:'100%', minHeight:70, background:c.notes,
                    border:`2px solid ${notes.trim()?c.border:'#ef4444'}`, borderRadius:8, padding:10,
                    fontSize:13, color:c.text, fontFamily:'inherit',
                    resize:'vertical', boxSizing:'border-box'}}/>
                {!notes.trim() && <div style={{color:'#ef4444', fontSize:11, marginTop:4}}>{rtl?'שדה חובה לפני סגירת האירוע':'Required before closing the event'}</div>}
              </div>
            )}

            {/* Modal buttons */}
            <div style={{padding:'12px 18px', background:c.topbar,
              borderTop:`2px solid ${c.border}`, display:'flex', gap:8, flexWrap:'wrap'}}>
              {selected.status==='new' &&
                btn(ack, '#1d4ed8','#fff','#3b82f6', t.ack, loading)}
              {showNotes &&
                btn(resolve,'#14532d','#bbf7d0','#4ade80', t.resolve, loading || !notes.trim())}
              <button onClick={onClose}
                style={{background:c.closeBg, color:c.closeTxt,
                  border:`1px solid ${c.border}`, borderRadius:7,
                  padding:'9px 14px', fontSize:13, fontWeight:600, cursor:'pointer',
                  marginInlineStart:'auto'}}>
                {t.close}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

